/**
 * Key-based data hook for PreseedDrawer
 *
 * KEY-BASED PATTERN: Fetches preseed data internally using preseedKey,
 * eliminating race conditions from dual state sources. The URL param
 * (preseedDrawerKey) is the single source of truth.
 *
 * This replaces the old pattern where:
 * 1. handleOpenPreseedDrawer() called setPreseed(preseed) synchronously
 * 2. A useEffect in drawers/index.tsx called setPreseed() when API completed
 *
 * When clicking quickly between rows, stale API responses would overwrite newer state.
 * Now the drawer fetches its own data based on the URL param key.
 *
 * @param preseedKey - The preseed key to fetch data for (from URL param preseedDrawerKey)
 * @returns Preseed data with count, loading state, and refetch function
 */

import { useMemo } from 'react';

import { useGenericSearch } from '@/hooks/useGenericSearch';
import { useMy } from '@/hooks/useMy';
import { Preseed, ResourceType } from '@/types';
import { mergeStatus } from '@/utils/api';
import { Regex } from '@/utils/regex.util';

export function usePreseedDrawerData(preseedKey: string) {
  // Parse preseed key to extract type and name for statistics query
  const [, preseedType, preseedName] = Regex.PRESEED_KEY.exec(preseedKey) || [];

  // Fetch preseed data by key
  const {
    data: preseedData,
    status: preseedStatus,
    refetch: refetchPreseed,
  } = useGenericSearch(
    {
      query: preseedKey,
      label: ResourceType.Preseed,
    },
    {
      enabled: Boolean(preseedKey),
      staleTime: 0, // Always fetch fresh data for drawer
    }
  );

  // Fetch count statistics for this preseed
  const { data: countData, status: countStatus } = useMy(
    {
      resource: 'statistic',
      query: `#preseed#${preseedType}#now#${preseedName}`,
    },
    {
      enabled:
        Boolean(preseedKey) && Boolean(preseedType) && Boolean(preseedName),
      staleTime: 0, // Always fetch fresh data for drawer
    }
  );

  // Combine preseed data with count
  const preseed = useMemo(() => {
    const rawPreseed = preseedData?.preseeds?.[0] as Preseed | undefined;
    if (!rawPreseed) return null;

    return {
      ...rawPreseed,
      count: countData?.[0]?.data?.count || 0,
    };
  }, [preseedData, countData]);

  // Merge statuses for loading state
  const combinedStatus = mergeStatus(preseedStatus, countStatus);

  return {
    preseed,
    isLoading: combinedStatus === 'pending',
    status: combinedStatus,
    refetch: refetchPreseed,
  };
}
